import styles from './Question.module.scss';
import Answers from './Answers';
import Button from '@ui/components/Button/Button';
import Card from '@ui/components/Card/Card';
import CardBody from '@ui/components/Card/components/CardBody';
import CardImage from '@ui/components/Card/components/CardImage';
import Content from '@ui/components/Content/Content';
import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import { InputMessage } from '@ui/components/InputField/InputField';
import { isEqual } from 'lodash';
import { Form, useFormStore } from '@ariakit/react';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

const Question = ({
  currentQuestionNumber,
  isRaffle,
  question: {
    answers,
    isCheckbox,
    questionDescription,
    questionImage,
    questionTitle,
  },
  setResult,
  setScore,
  setStep,
  totalQuestionNumber,
}) => {
  const [values, setValues] = useState({
    answer: isCheckbox ? [] : '',
  });

  const form = useFormStore({ values, setValues });

  const [isAnswered, setIsAnswered] = useState(false);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false);

  const findNextValidStep = () => {
    if (currentQuestionNumber === totalQuestionNumber && !isRaffle) {
      setStep(prev => prev + 2);
    } else {
      setStep(prev => prev + 1);
    }
  };

  const checkSelections = () => {
    // Filters out incorrect answers, takes only answer property, sorts them alphabetically and joins everything to a string.
    const correctAnswers = answers
      .filter(({ isCorrect }) => isCorrect)
      .map(({ answer }) => answer)
      .sort()
      .join(',');

    const selectedAnswers = isCheckbox
      ? values.answer.sort().join(',')
      : values.answer;

    const isAnsweredCorrectly = isEqual(correctAnswers, selectedAnswers);

    setIsAnswerCorrect(isAnsweredCorrectly);
    setIsAnswered(true);

    setResult(prevResult => [
      ...prevResult,
      {
        question: questionTitle,
        userAnswer: selectedAnswers,
        richtig: isAnsweredCorrectly,
      },
    ]);
  };

  const proceedToNextStep = () => {
    setIsAnswered(false);

    if (isAnswerCorrect) {
      setScore(currentScore => currentScore + 1);
    }

    form.reset();
    findNextValidStep();
  };

  useEffect(() => {
    // Resets answer value each time the question changes.
    setValues({ answer: isCheckbox ? [] : '' });
  }, [currentQuestionNumber]);

  return (
    <Form store={form}>
      <Card className={styles.questionCard}>
        <CardImage
          aspectRatio={0.49}
          image={{
            ...questionImage,
            ...{
              src: questionImage.url,
              alt: questionImage.alt || questionImage.title,
            },
          }}
        />
        <CardBody>
          <Content noSpacing>
            {`${currentQuestionNumber} von ${totalQuestionNumber}`}
          </Content>
          <Answers
            answers={answers}
            isAnswerCorrect={isAnswerCorrect}
            isAnswered={isAnswered}
            isCheckbox={isCheckbox}
            questionTitle={questionTitle}
            store={form}
          />
          <InputMessage name="answer" store={form} />
          {isAnswered && (
            <div
              className={classNames('pull-down-animation', styles.answerInfo, {
                'big-entrance-animation': isAnswerCorrect,
                [styles.answerInfoCorrect]: isAnswerCorrect,
              })}
            >
              <div className={styles.result}>
                <span
                  className={classNames(styles.checkIconWrapper, {
                    [styles.exclamationIconWrapper]: !isAnswerCorrect,
                  })}
                >
                  <FontAwesomeProIcon
                    icon={isAnswerCorrect ? 'check' : 'exclamation'}
                    role="presentation"
                    size="lg"
                    type="solid"
                  />
                </span>
                {isAnswerCorrect ? 'Richtig!' : 'Nicht richtig'}
              </div>
              {!!questionDescription && (
                <Content noSpacing withParser>
                  {questionDescription}
                </Content>
              )}
            </div>
          )}
          <Button
            className={styles.questionButton}
            isElevated
            onClick={isAnswered ? proceedToNextStep : checkSelections}
          >
            Weiter
          </Button>
        </CardBody>
      </Card>
    </Form>
  );
};

Question.propTypes = {
  currentQuestionNumber: PropTypes.string,
  isRaffle: PropTypes.bool,
  question: PropTypes.shape({
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        answer: PropTypes.string,
        isCorrect: PropTypes.string,
      }),
    ),
    isCheckbox: PropTypes.string,
    questionDescription: PropTypes.string,
    questionImage: PropTypes.shape({
      alt: PropTypes.string,
      title: PropTypes.string,
      url: PropTypes.string,
    }),
    questionTitle: PropTypes.string,
  }),
  setResult: PropTypes.func,
  setScore: PropTypes.func,
  setStep: PropTypes.func,
  totalQuestionNumber: PropTypes.string,
};

export default Question;
